import type { ApiEnvelope } from '@/types/api'
import type { Choice, LogicJump, PublicForm, Question } from '@/types/form'
import type { MiaolinDynamicOption } from './miaolin-channel-options'

export const MIAOLIN_DEPARTMENT_QUESTION_ID = 'q-miaolin-24'

export type MiaolinDepartmentSalesUsersResult = {
  enabled: boolean
  providerMode: 'd1'
  providerState: 'configured' | 'empty' | 'schema_missing' | 'not_configured'
  source: 'miaolin_team_sales_users_d1'
  department: string
  teamCode: string
  options: MiaolinDynamicOption[]
  fallbackUsed: boolean
  safeMessage?: string
}

export async function fetchMiaolinDepartmentSalesUserOptions({
  formId,
  department,
  signal,
}: {
  formId: string
  department: string
  signal?: AbortSignal
}): Promise<MiaolinDepartmentSalesUsersResult | null> {
  const trimmedDepartment = department.trim()
  if (!trimmedDepartment) return null

  try {
    const response = await fetch(
      `/api/public/forms/${encodeURIComponent(formId)}/miaolin-team-sales-users`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          department: trimmedDepartment,
          teamCode: extractDepartmentCode(trimmedDepartment),
        }),
        signal,
      },
    )
    if (!response.ok) return null

    const envelope = (await response.json()) as ApiEnvelope<MiaolinDepartmentSalesUsersResult>
    if (!envelope.data?.enabled || envelope.data.options.length === 0) return null
    return envelope.data
  } catch {
    return null
  }
}

export function getMiaolinDepartmentQuestionId(questions: Question[]) {
  return questions.find((question) => isMiaolinDepartmentQuestion(question))?.id ?? null
}

export function isMiaolinDepartmentWebhookQuestion(question: Question) {
  return isMiaolinDepartmentQuestion(question)
}

export function getMiaolinDepartmentSalesQuestionIds(form: PublicForm) {
  const departmentQuestionId =
    getMiaolinDepartmentQuestionId(form.questions) ?? MIAOLIN_DEPARTMENT_QUESTION_ID
  const ids = new Set<string>()
  for (const jump of form.logicJumps) {
    if (jump.questionId !== departmentQuestionId) continue
    const targetIds = getLogicJumpTargetQuestionIds(jump)
    for (const targetId of targetIds) ids.add(targetId)
  }
  return Array.from(ids)
}

export function buildMiaolinDepartmentSalesWebhookForm({
  baseForm,
  department,
  options,
}: {
  baseForm: PublicForm
  department: string
  options: MiaolinDynamicOption[]
}): PublicForm {
  const departmentQuestionId =
    getMiaolinDepartmentQuestionId(baseForm.questions) ?? MIAOLIN_DEPARTMENT_QUESTION_ID
  const targetQuestionIds = getDepartmentTargetQuestionIds(baseForm, departmentQuestionId, department)
  if (targetQuestionIds.size === 0) return baseForm

  return cloneFormWithRuntimeShape(baseForm, {
    questions: baseForm.questions.map((question) => {
      if (!targetQuestionIds.has(question.id)) return question
      return {
        ...question,
        choices: createMiaolinChoices(question.id, options),
        settings: {
          ...question.settings,
          dynamicOptions: {
            kind: 'miaolin_team_sales_users',
            providerMode: 'd1',
            sourceLabel: 'D1 sales users by team_code',
            controllerQuestionId: departmentQuestionId,
            controllerValue: department,
            controllerTeamCode: extractDepartmentCode(department),
            providerState: 'loaded',
            fallback: 'static_question_choices',
          },
        },
      } satisfies Question
    }),
    logicJumps: baseForm.logicJumps,
  })
}

function getDepartmentTargetQuestionIds(
  form: PublicForm,
  departmentQuestionId: string,
  department: string,
) {
  const targetQuestionIds = new Set<string>()
  for (const jump of form.logicJumps) {
    if (jump.questionId !== departmentQuestionId) continue
    if (!logicJumpMatchesDepartment(jump, department)) continue
    for (const targetId of getLogicJumpTargetQuestionIds(jump)) targetQuestionIds.add(targetId)
  }
  return targetQuestionIds
}

function logicJumpMatchesDepartment(jump: LogicJump, department: string) {
  return jump.conditions.some(
    (condition) =>
      condition.operator === 'equals' &&
      typeof condition.value === 'string' &&
      condition.value.trim() === department.trim(),
  )
}

function getLogicJumpTargetQuestionIds(jump: LogicJump) {
  const ids = new Set<string>()
  if (typeof jump.targetQuestionId === 'string') ids.add(jump.targetQuestionId)
  for (const target of jump.displayTargets ?? []) {
    if ('questionId' in target && typeof target.questionId === 'string') ids.add(target.questionId)
  }
  return Array.from(ids)
}

function cloneFormWithRuntimeShape(
  baseForm: PublicForm,
  patch: Pick<PublicForm, 'questions' | 'logicJumps'>,
): PublicForm {
  const questionIds = new Set(patch.questions.map((question) => question.id))
  return {
    ...baseForm,
    questions: patch.questions,
    logicJumps: patch.logicJumps,
    questionGroups: baseForm.questionGroups?.map((group) => ({
      ...group,
      questionIds: group.questionIds.filter((questionId) => questionIds.has(questionId)),
      questions: group.questions?.filter((question) => questionIds.has(question.id)),
    })),
  }
}

function createMiaolinChoices(questionId: string, options: MiaolinDynamicOption[]): Choice[] {
  return options.map((option, index) => ({
    id: `${questionId}-team-d1-choice-${index + 1}`,
    questionId,
    label: option.label,
    value: option.value,
    orderIndex: index,
  }))
}

function extractDepartmentCode(department: string) {
  return department.match(/\b\d{2,5}\b/)?.[0] ?? ''
}

function isMiaolinDepartmentQuestion(question: Question) {
  return (
    question.id === MIAOLIN_DEPARTMENT_QUESTION_ID ||
    question.settings.sourceQuestionId === MIAOLIN_DEPARTMENT_QUESTION_ID ||
    question.settings.customerFieldTarget === 'department' ||
    getQuestionDbFieldName(question) === 'department' ||
    question.title === '部門別'
  )
}

function getQuestionDbFieldName(question: Question) {
  const dbFieldName = question.settings.dbFieldName
  if (typeof dbFieldName === 'string') return dbFieldName
  const sharedDbFieldName = question.settings.sharedDbFieldName
  return typeof sharedDbFieldName === 'string' ? sharedDbFieldName : null
}
